/**
 * LootTable - Defines what items enemies can drop
 */

import { Item } from '../items/Item';
import { ItemRegistry } from '../items/ItemRegistry';

interface LootEntry {
  itemId: string;
  chance: number; // 0-1 probability
  minQuantity: number;
  maxQuantity: number;
}

interface LootTableConfig {
  guaranteed: LootEntry[]; // Always drops
  random: LootEntry[]; // Random chance to drop
  maxDrops: number; // Max random items that can drop
}

// Loot tables for each enemy type
const LOOT_TABLES: { [enemyType: string]: LootTableConfig } = {
  zombie: {
    guaranteed: [],
    random: [
      { itemId: 'raw_meat', chance: 0.25, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'bone', chance: 0.15, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  skeleton: {
    guaranteed: [],
    random: [
      { itemId: 'bone', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'bone_club', chance: 0.03, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  spider: {
    guaranteed: [],
    random: [
      { itemId: 'spider_eye', chance: 0.3, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'fiber', chance: 0.2, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'spider_fang', chance: 0.04, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  wolf: {
    guaranteed: [],
    random: [
      { itemId: 'raw_meat', chance: 0.5, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'wolf_pelt', chance: 0.2, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'wolf_fang_dagger', chance: 0.05, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  orc: {
    guaranteed: [],
    random: [
      { itemId: 'raw_meat', chance: 0.4, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'orc_cleaver', chance: 0.06, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'iron_ore', chance: 0.15, minQuantity: 1, maxQuantity: 2 },
      { itemId: 'health_potion', chance: 0.08, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  golem: {
    guaranteed: [],
    random: [
      { itemId: 'stone', chance: 0.6, minQuantity: 2, maxQuantity: 4 },
      { itemId: 'golem_core', chance: 0.1, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'golem_hammer', chance: 0.04, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'iron_ore', chance: 0.25, minQuantity: 1, maxQuantity: 2 },
    ],
    maxDrops: 1
  },
  demon: {
    guaranteed: [],
    random: [
      { itemId: 'demon_heart', chance: 0.08, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'demon_blade', chance: 0.03, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'health_potion', chance: 0.15, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  },
  ghost: {
    guaranteed: [],
    random: [
      { itemId: 'ectoplasm', chance: 0.25, minQuantity: 1, maxQuantity: 1 },
      { itemId: 'spectral_scythe', chance: 0.04, minQuantity: 1, maxQuantity: 1 },
    ],
    maxDrops: 1
  }
};

export interface LootDrop {
  item: Item;
  quantity: number;
}

export class LootTable {
  /** Generate loot drops for a killed enemy */
  static generateLoot(enemyType: string): LootDrop[] {
    const table = LOOT_TABLES[enemyType];
    if (!table) {
      console.warn(`No loot table for enemy type: ${enemyType}`);
      return [];
    }

    const drops: LootDrop[] = [];

    // Process guaranteed drops
    for (const entry of table.guaranteed) {
      const item = ItemRegistry.get(entry.itemId);
      if (item) {
        const quantity = this.randomInt(entry.minQuantity, entry.maxQuantity);
        drops.push({ item, quantity });
      }
    }

    // Process random drops
    let randomDropCount = 0;
    for (const entry of table.random) {
      if (randomDropCount >= table.maxDrops) break;

      if (Math.random() < entry.chance) {
        const item = ItemRegistry.get(entry.itemId);
        if (item) {
          const quantity = this.randomInt(entry.minQuantity, entry.maxQuantity);
          drops.push({ item, quantity });
          randomDropCount++;
        }
      }
    }

    return drops;
  }

  private static randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
